/**
 * SEO Meta Tags Utility
 * Handles dynamic meta tags for better SEO
 */

export interface MetaTags {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
}

const defaultMeta: MetaTags = {
  title: 'MatrixHub Company Ltd. | Innovative Technology Solutions',
  description: 'Your trusted partner in delivering innovative solutions and exceptional services to drive business success. We provide custom web development, mobile apps, AI-powered SaaS solutions, and more.',
  keywords: 'MatrixHub, technology solutions, web development, mobile apps, SaaS, AI solutions, software development, Tanzania, Dodoma, EMR system, school management system, healthcare software',
  image: '/img/Logo.png',
  url: 'https://matrixhub.co.tz',
  type: 'website',
  author: 'MatrixHub Company Ltd.'
};

export function updateMetaTags(meta: MetaTags = {}) {
  const mergedMeta = { ...defaultMeta, ...meta };
  const baseUrl = window.location.origin;
  const fullUrl = meta.url || `${baseUrl}${window.location.pathname}`;
  const imageUrl = meta.image ? (meta.image.startsWith('http') ? meta.image : `${baseUrl}${meta.image}`) : `${baseUrl}${defaultMeta.image}`;

  // Update title
  if (mergedMeta.title) {
    document.title = mergedMeta.title;
  }

  // Update or create meta tags
  const updateOrCreateMeta = (name: string, content: string, attribute = 'name') => {
    let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  // Basic meta tags
  updateOrCreateMeta('description', mergedMeta.description || '');
  updateOrCreateMeta('keywords', mergedMeta.keywords || '');
  updateOrCreateMeta('author', mergedMeta.author || '');
  updateOrCreateMeta('robots', 'index, follow');
  updateOrCreateMeta('googlebot', 'index, follow');
  updateOrCreateMeta('viewport', 'width=device-width, initial-scale=1.0');

  // Open Graph tags
  updateOrCreateMeta('og:title', mergedMeta.title || '', 'property');
  updateOrCreateMeta('og:description', mergedMeta.description || '', 'property');
  updateOrCreateMeta('og:image', imageUrl, 'property');
  updateOrCreateMeta('og:url', fullUrl, 'property');
  updateOrCreateMeta('og:type', mergedMeta.type || 'website', 'property');
  updateOrCreateMeta('og:site_name', 'MatrixHub Company Ltd.', 'property');
  updateOrCreateMeta('og:locale', 'en_US', 'property');

  // Twitter Card tags
  updateOrCreateMeta('twitter:card', 'summary_large_image');
  updateOrCreateMeta('twitter:title', mergedMeta.title || '');
  updateOrCreateMeta('twitter:description', mergedMeta.description || '');
  updateOrCreateMeta('twitter:image', imageUrl);
  updateOrCreateMeta('twitter:site', '@matrixhubco');
  updateOrCreateMeta('twitter:creator', '@matrixhubco');

  // Canonical URL
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', fullUrl);

  // Language
  const html = document.documentElement;
  html.setAttribute('lang', 'en');
}

export function addStructuredData(data: object) {
  // Remove existing structured data script if any
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MatrixHub Company Ltd.",
    "alternateName": "MatrixHub",
    "url": "https://matrixhub.co.tz",
    "logo": "https://matrixhub.co.tz/img/Logo.png",
    "description": "Your trusted partner in delivering innovative solutions and exceptional services to drive business success.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Capital CityMall, Office #57, P. O. Box 2763 Dodoma",
      "addressLocality": "Dodoma",
      "addressCountry": "TZ"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+255-754-690-060",
      "contactType": "Customer Service",
      "email": "info@matrixhub.co.tz",
      "areaServed": "TZ",
      "availableLanguage": ["en", "sw"]
    },
    "sameAs": [
      "https://x.com/matrixhubco",
      "https://web.facebook.com/matrixhubco",
      "https://www.instagram.com/matrixhubco/",
      "https://tz.linkedin.com/company/matrixhubco"
    ]
  };
}

export function getLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://matrixhub.co.tz",
    "name": "MatrixHub Company Ltd.",
    "image": "https://matrixhub.co.tz/img/Logo.png",
    "description": "Technology solutions provider offering web development, mobile apps, SaaS platforms, and AI-powered software solutions.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Capital CityMall, Office #57, P. O. Box 2763 Dodoma",
      "addressLocality": "Dodoma",
      "addressRegion": "Dodoma",
      "postalCode": "2763",
      "addressCountry": "TZ"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-6.1630",
      "longitude": "35.7516"
    },
    "url": "https://matrixhub.co.tz",
    "telephone": "+255-754-690-060",
    "priceRange": "$$",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday", "Sunday"],
        "opens": "09:00",
        "closes": "13:00"
      }
    ]
  };
}

export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "MatrixHub Company Ltd.",
    "url": "https://matrixhub.co.tz",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://matrixhub.co.tz/?s={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };
}

export function getServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Technology Solutions",
    "provider": {
      "@type": "Organization",
      "name": "MatrixHub Company Ltd.",
      "url": "https://matrixhub.co.tz"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Tanzania"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Technology Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Custom Web Development"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mobile App Solutions"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "UI/UX Design"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cybersecurity Solutions"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Marketing"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Cloud Computing"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI-Powered Solutions"
          }
        }
      ]
    }
  };
}

export function getProductSchemas() {
  return [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "SAAS - School Academic & Administration System",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web, iOS, Android",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "An AI-powered comprehensive web and mobile platform designed for efficient management of school operations.",
      "provider": {
        "@type": "Organization",
        "name": "MatrixHub Company Ltd."
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "MediCore EMR",
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web, iOS, Android",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "An AI-powered Electronic Medical Records (EMR) system for managing all hospital operations from patient entry to discharge.",
      "provider": {
        "@type": "Organization",
        "name": "MatrixHub Company Ltd."
      },
      "brand": {
        "@type": "Brand",
        "name": "AfyaMatrix AI"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Senda",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "A bulk SMS and messaging gateway for transactional messages, OTPs, notifications, and marketing campaigns via web dashboard, REST API, and SMPP.",
      "provider": {
        "@type": "Organization",
        "name": "MatrixHub Company Ltd."
      },
      "brand": {
        "@type": "Brand",
        "name": "MatrixGate"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NiPrintie",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "iOS, Android",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "A mobile application solution designed to assist university students with printing services.",
      "provider": {
        "@type": "Organization",
        "name": "MatrixHub Company Ltd."
      }
    }
  ];
}

export function getBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
}
